import styled, { keyframes } from 'styled-components';
import Autocomplete from 'react-google-autocomplete';

const fieldHighlight = keyframes`
  0%   { outline: 3px solid transparent; }
  15%  { outline: 3px solid rgba(58, 154, 131, 0.5); }
  35%  { outline: 3px solid transparent; }
  55%  { outline: 3px solid rgba(58, 154, 131, 0.5); }
  75%  { outline: 3px solid transparent; }
  100% { outline: 3px solid transparent; }
`;

export const InputPageWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  column-gap: 3rem;
  grid-auto-rows: min-content;
  font-size: ${(props) => props.theme.fontSizeXl};
  margin-right: 3rem;
  margin-left: 3rem;

  @media only screen and (max-width: ${(props) => props.theme.breakpoint1200}) {
    column-gap: 1.5rem;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: flex;
    flex-direction: column;
    align-items: left;
    gap: 0.5rem;
    margin-left: 0%;
    margin-right: 0%;
    padding-bottom: ${(props) => (props.$showValidation ? '4rem' : '2rem')};
  }
`;

export const InputPageTitle = styled.h1`
  font-size: ${(props) => props.theme.fontSizeTitle};
  color: ${(props) => props.theme.titleColor};
  grid-row: 1;
  grid-column: 1 / span 6;
  text-align: left;
  height: fit-content;
  grid-row: ${(props) => props.$row} / span 1;
  grid-column: ${(props) => props.$column} / span
    ${(props) => (props.$span ? props.$span : 6)};

  @media only screen and (max-width: ${(props) => props.theme.breakpointSm}) {
    font-size: ${(props) => props.theme.fontSizeMobileTitle};
    margin-bottom: 0;
  }
`;

export const InputPageSubtitle = styled.div`
  font-size: ${(props) => props.theme.fontSizeBase};
  color: ${(props) => props.theme.subTitleColor};
  text-align: left;
  grid-row: 2;
  grid-column: 1 / span 6;

  margin: 0.5rem 0.5rem;

  b {
    font-weight: bold;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointSm}) {
    margin: 0.5rem 0.25rem;
  }
`;

export const InputPageQuestion = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  margin: ${(props) => (props.$spacious ? '3.5rem' : '1.5rem')} 0 ${(props) => (props.$slim ? '0.75rem' : props.$spacious ? '4rem' : '2rem')} 0;
  padding-right: ${(props) => (props.$paddingRight ? props.$paddingRight : '3rem')};
  padding-left: ${(props) => (props.$paddingLeft ? props.$paddingLeft : '0')};
  height: fit-content;
  grid-row: ${(props) => props.$row} / span
    ${(props) => (props.$rowSpan ? props.$rowSpan : 1)};
  grid-column: ${(props) => props.$column} / span
    ${(props) => (props.$colSpan ? props.$colSpan : 2)};

  @media only screen and (min-width: 1001px) and (max-width: ${(props) => props.theme.breakpoint1450}) {
    ${(props) =>
      props.$midRow !== undefined && `grid-row: ${props.$midRow} / span 1;`}
    ${(props) =>
      props.$midColumn !== undefined &&
      `grid-column: ${props.$midColumn} / span ${props.$midColSpan || 2};`}
  }

  @media only screen and (min-width: 1001px) and (max-width: ${(props) => props.theme.breakpoint1200}) {
    padding-right: ${(props) => (props.$paddingRight ? props.$paddingRight : '1.5rem')};
    padding-left: ${(props) => (props.$paddingLeft ? props.$paddingLeft : '0')};
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    padding: 0;
    margin-bottom: 1rem;
  }

  &.flash {
    border-radius: 4px;
    outline-offset: 5px;
    animation: ${fieldHighlight} 2s ease-in-out;
  }
`;

export const SectionRow = styled.div`
  grid-row: ${(props) => props.$row};
  grid-column: 1 / span 6;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  column-gap: 3rem;

  @media only screen and (max-width: ${(props) => props.theme.breakpoint1200}) {
    column-gap: 1.5rem;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: contents;
  }
`;

export const InputPageQuestionText = styled.span`
  font-size: ${(props) => props.theme.fontSizeMd};
  font-weight: 600;
  color: ${(props) => props.theme.secondaryColor};
  display: inline-block;
  text-align: left;
  margin-bottom: 1rem;
  vertical-align: top;

  @media only screen and (max-width: ${(props) => props.theme.breakpoint1200}) {
    font-size: ${(props) => props.theme.fontSizeBase};
  }
`;

export const QuestionLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 1rem;
  width: 100%;

  ${InputPageQuestionText} {
    margin-bottom: 0;
  }
`;

export const SubmitButton = styled.button`
  background-color: ${(props) => props.theme.primaryColor};
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  height: 3rem;
  font-size: ${(props) => props.theme.fontSizeLg};

  &:hover {
    background-color: ${(props) => props.theme.primaryColorDark};
  }

  &:disabled {
  color: ${(props) => props.theme.disabledColor};
    background-color: ${(props) => props.theme.disabledBg};
    cursor: not-allowed;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    height: 100%;
    border-radius: 0;
  }
`;

export const SubmitButtonWrapper = styled.div`
  grid-row: ${(props) => props.$row || 99} / span 1;
  grid-column: ${(props) => props.$column} / span 6;
  position: relative;
  width: 100%;
  align-self: center;
  margin-bottom: 4rem;

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    position: fixed;
    width: 100vw;
    margin: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    overflow: hidden;
    height: ${(props) => (props.$hideOnMobile ? '0' : '4rem')};
    transition: height 0.3s ease-in-out;
  }
`;

export const StyledPlaceInput = styled(Autocomplete)`
  background-color: white;
  border: 1.5px solid ${(props) => props.theme.bgColor};
  height: 2.5rem;
  border-radius: ${(props) => props.theme.borderRadius};
  width: ${(props) => (props.width ? props.width : '80%')};
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  font-size: ${(props) => props.theme.fontSizeBase};
  padding: 0 0.6rem;
`;

export const WarningText = styled.p`
  color: ${(props) => props.theme.warningColor};
  font-size: ${(props) => props.theme.fontSizeBase};
  width: 100%;
  text-align: left;
`;

export const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1.25rem;
`;

export const CheckboxText = styled.span`
  font-size: ${(props) => props.theme.fontSizeMd};
  color: ${(props) => props.theme.secondaryColor};
  display: inline-block;
  text-align: left;
  vertical-align: top;
  width: 100%;
`;

export const GrayWarningText = styled.p`
  color: ${(props) => props.theme.disabledColor};
  font-size: ${(props) => props.theme.fontSizeBase};
  width: 100%;
  text-align: left;
  margin-top: 0.6rem;
  margin-bottom: 0.6rem;
  font-style: italic;
`;

export const CaasWarningText = styled.p`
  color: ${(props) => props.theme.secondaryColor};
  font-size: ${(props) => props.theme.fontSizeBase};
  width: 100%;
  text-align: left;
  margin-top: 0.6rem;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

export const ValidationTooltip = styled.div`
  position: absolute;
  bottom: 3.75rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 1rem;
  border-radius: ${(props) => props.theme.borderRadius};
  min-width: 18.75rem;
  z-index: 1000;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};
  transition: opacity 0.2s;

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: none;
  }

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 0.5rem solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

export const ValidationTooltipTitle = styled.div`
  font-size: ${(props) => props.theme.fontSizeBase};
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

export const ValidationTooltipList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const ValidationTooltipItem = styled.li`
  font-size: ${(props) => props.theme.fontSizeSm};
  margin: 0.3rem 0;
  text-align: left;
  &:before {
    content: '• ';
    margin-right: 0.5rem;
  }
`;

export const MobileValidationSection = styled.div`
  display: none;

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: block;
    position: fixed;
    bottom: ${(props) => (props.$submitVisible ? '4rem' : '0')};
    left: 0;
    width: 100vw;
    z-index: 99;
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    max-height: ${(props) => (props.$show ? '12rem' : '0')};
    overflow-y: auto;
    padding: ${(props) => (props.$show ? '0.75rem 1rem' : '0 1rem')};
    cursor: pointer;
    transition:
      max-height 0.3s ease-in-out,
      padding 0.3s ease-in-out,
      bottom 0.3s ease-in-out;
  }
`;

export const ToggleAddressInputStyleButton = styled.button`
  padding: 0;
  margin: 0;
  font-size: inherit;
  font-family: inherit;
  font-style: inherit;
  border: none;
  text-decoration: underline;
  background: none;

  &:hover {
    cursor: pointer;
    color: ${(props) => props.theme.primaryColor};
  }
`;

export const IncentivesSection = styled.div`
  background-color: ${(props) => props.theme.bgColor};
  border-radius: 3px;
  padding: 1.5rem;
  margin: 1rem 0 3rem 0;
  grid-row: ${(props) => props.$row} / span 1;
  grid-column: ${(props) => props.$column} / span
    ${(props) => (props.$colSpan ? props.$colSpan : 2)};

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    margin-bottom: 4.5rem;
  }
`;

export const IncentiveTitle = styled.h3`
  font-weight: 700;
  text-align: left;
  color: ${(props) => props.theme.titleColor};
  font-size: ${(props) => props.theme.fontSizeLg};
  margin: 0 0 0.5rem 0;
`;

export const IncentivesDescription = styled.p`
  text-align: left;
  font-weight: 500;
  font-size: ${(props) => props.theme.fontSizeBase};
  color: ${(props) => props.theme.secondaryColor};
  margin-bottom: 1.5rem;
`;

export const IncentiveInputContainer = styled.div`
  display: flex;
  gap: 1.5rem;

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    flex-direction: column;
  }
`;

export const IncentiveInput = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  ${InputPageQuestionText} {
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

export const InputDescription = styled.span`
  text-align: left;
  width: 100%;
  font-size: ${(props) => props.theme.fontSizeBase};
  font-weight: 700;
  color: ${(props) => props.theme.secondaryColor};
  margin-top: 0.5rem;

  @media only screen and (max-width: ${(props) => props.theme.breakpointSm}) {
    font-size: ${(props) => props.theme.fontSizeSm};
  }
`;

export const WorkplaceEvCount = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${(props) => props.theme.secondaryColorLight};
  border-radius: ${(props) => props.theme.borderRadius};
  margin-bottom: 1rem;
  padding: 1.5rem 2rem;
  width: 100%;
  height: 100%;
  grid-row: ${(props) => props.$row} / span
    ${(props) => (props.$rowSpan ? props.$rowSpan : 2)};
  grid-column: ${(props) => props.$column} / span
    ${(props) => (props.$colSpan ? props.$colSpan : 2)};

  @media only screen and (max-width: ${(props) => props.theme.breakpointLg}) {
    padding: 3rem 1rem;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: none;
  }
`;

export const WorkplaceEvCountTitle = styled.h2`
  font-size: ${(props) => props.theme.fontSizeXl};
  color: ${(props) => props.theme.secondaryColor};
  text-align: center;
  margin: 0;
  justify-self: flex-start;

  @media only screen and (max-width: ${(props) => props.theme.breakpointLg}) {
    font-size: ${(props) => props.theme.fontSizeLg};
    margin: 0 0 0.5rem 0;
  }
`;

export const WorkplaceEvCountSection = styled.div`
  width: 100%;
`;

export const WorkplaceEvCountSectionTitle = styled.h4`
  color: ${(props) => props.theme.secondaryColor};
  font-weight: 700;
  text-align: center;
  margin: 0 0 0.4rem 0;
  font-size: ${(props) => props.theme.fontSizeSm};
`;

export const WorkplaceEvCountValue = styled.p`
  color: ${(props) => props.theme.secondaryColor};
  font-weight: bold;
  font-size: ${(props) => props.theme.fontSizeXl};
  margin: 0;

  @media only screen and (max-width: ${(props) => props.theme.breakpointLg}) {
    font-size: ${(props) => props.theme.fontSizeLg};
  }
`;

export const VerticalSpacer = styled.div`
  padding: 0.5rem;
  grid-row: ${(props) => props.$row} / span 1;
  grid-column: 1 / span 6;

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    display: none;
  }
`;
